import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconEdit, IconLoader2, IconCheck } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { useMyProfile, useUpdateMyProfile } from '../../hooks/useEmployees';

const EDITABLE_FIELDS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'middle_name', label: 'Middle Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'phone_number', label: 'Phone Number' },
  { key: 'address', label: 'Address' },
  { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
];

const READ_ONLY_FIELDS = [
  { key: 'employee_no', label: 'Employee No.' },
  { key: 'work_email', label: 'Work Email' },
  { key: 'job_title', label: 'Job Title' },
  { key: 'grade_level', label: 'Grade Level' },
  { key: 'hire_date', label: 'Hire Date' },
  { key: 'employment_status', label: 'Employment Status' },
  { key: 'gender', label: 'Gender' },
  { key: 'department_name', label: 'Department' },
  { key: 'primary_unit_name', label: 'Unit' },
  { key: 'line_manager_name', label: 'Line Manager' },
];

export default function BasicInfoModal({ isOpen, onClose }) {
  const { data: profile, isLoading } = useMyProfile(isOpen);
  const updateProfile = useUpdateMyProfile();

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (profile) {
      const initial = {};
      EDITABLE_FIELDS.forEach(f => { initial[f.key] = profile[f.key] || ''; });
      setFormData(initial);
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync(formData);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch {
      // interceptor handles error toast
    }
  };

  const handleCancel = () => {
    const reset = {};
    EDITABLE_FIELDS.forEach(f => { reset[f.key] = profile?.[f.key] || ''; });
    setFormData(reset);
    setEditing(false);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[3px]"
        style={{ zIndex: 99998 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[680px] max-h-[85vh] flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden" style={{ zIndex: 99999 }}>

        {/* Header */}
        <div className="px-20 py-15 border-b border-gray-100 bg-gray-50 flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Account Settings</p>
            <h3 className="text-sm font-bold text-gray-800">Basic Information</h3>
          </div>
          <div className="flex items-center gap-8">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-6 px-12 py-6 text-[12px] font-bold text-primary border border-primary rounded hover:bg-primary/5 transition-colors"
              >
                <IconEdit size={13} />
                Edit
              </button>
            )}
            <button onClick={onClose} className="p-5 rounded hover:bg-gray-200 text-gray-400 transition-colors">
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-20 py-20">
          {isLoading ? (
            <div className="flex justify-center py-40">
              <IconLoader2 size={24} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Full name banner */}
              <div className="flex items-center gap-15 mb-20 p-15 bg-primary/5 rounded-lg border border-primary/10">
                <div className="w-50 h-50 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {profile?.first_name?.[0]?.toUpperCase()}{profile?.last_name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="text-[15px] font-bold text-gray-800">{profile?.full_name || '—'}</p>
                  <p className="text-[12px] text-gray-500">{profile?.work_email}</p>
                  <p className="text-[11px] text-gray-400 mt-2">{profile?.job_title} · {profile?.department_name}</p>
                </div>
              </div>

              {/* Editable fields */}
              <div className="mb-20">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-12">Personal Details</p>
                <div className="grid grid-cols-2 gap-15">
                  {EDITABLE_FIELDS.map(field => (
                    <div key={field.key}>
                      <label className="text-[10px] font-bold text-gray-500 uppercase block mb-5">{field.label}</label>
                      {editing ? (
                        <input
                          type={field.type || 'text'}
                          value={formData[field.key] || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                          className="w-full text-[13px] border border-gray-200 rounded px-10 py-8 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      ) : (
                        <p className="text-[13px] text-gray-800 font-medium">{profile?.[field.key] || '—'}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Read-only fields */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-12">Employment Details</p>
                <div className="grid grid-cols-2 gap-15">
                  {READ_ONLY_FIELDS.map(field => (
                    <div key={field.key}>
                      <label className="text-[10px] font-bold text-gray-500 uppercase block mb-5">{field.label}</label>
                      <p className="text-[13px] text-gray-800 font-medium capitalize">{profile?.[field.key] || '—'}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer — only when editing */}
        {editing && (
          <div className="px-20 py-12 border-t border-gray-100 bg-gray-50 flex justify-end gap-10 shrink-0">
            <button
              onClick={handleCancel}
              className="btn btn-outline-secondary btn-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateProfile.isPending}
              className="btn btn-primary btn-sm flex items-center gap-6 disabled:opacity-50"
            >
              {updateProfile.isPending
                ? <><IconLoader2 size={13} className="animate-spin" /> Saving...</>
                : <><IconCheck size={13} /> Save Changes</>
              }
            </button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
